"use client";

/**
 * Badge — 상태 배지 (우선순위 10)
 *
 * 표의 상태 컬럼, 라벨 등에 쓰는 알약형 배지.
 * - text: 표시할 내용 (children 으로 넣어도 된다)
 * - color: 색상. 프리셋("gray"|"primary"|"green"|"red"|"amber"|"yellow"|"blue") 또는
 *          임의의 CSS 색상 문자열("#16a34a", "oklch(...)" 등)을 넣을 수 있다.
 *          프리셋이 아닌 색은 글자색을 검정과 섞어 밝은 색(gold 등)도 대비가 보장된다.
 * - size: 크기 ("sm"|"md"|"lg")
 *
 * 프리셋이 아닌 색을 주면 해당 색을 글자색으로, 옅은 톤을 배경으로 자동 계산한다.
 *
 * @example
 * <Badge text="활성" color="green" />
 * <Badge text="정지" color="red" size="sm" />
 * <Badge text="VIP" color="#7c3aed" />   // 커스텀 색
 */
import { type CSSProperties, type ComponentProps, type ReactNode } from "react";

import { cn } from "../../lib/utils";

export type BadgeColor =
  | "gray"
  | "primary"
  | "green"
  | "red"
  | "amber"
  | "yellow"
  | "blue";

export type BadgeSize = "sm" | "md" | "lg";

export interface BadgeProps extends ComponentProps<"span"> {
  /** 표시할 텍스트 (children 으로 대체 가능) */
  text?: ReactNode;
  /** 프리셋 색상 또는 임의의 CSS 색상 문자열 */
  color?: BadgeColor | (string & {});
  /** 크기 (기본 md) */
  size?: BadgeSize;
}

// 프리셋 색상 (라이트/다크 모두 대응하는 옅은 톤 + 진한 글자 + 테두리)
const PRESET: Record<BadgeColor, string> = {
  gray: "bg-muted text-muted-foreground border-border",
  primary: "bg-primary/10 text-primary border-primary/20",
  green:
    "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30",
  red: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30",
  amber:
    "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30",
  yellow:
    "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30",
  blue: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/30",
};

const SIZE: Record<BadgeSize, string> = {
  sm: "h-5 px-1.5 text-[11px]",
  md: "h-6 px-2 text-xs",
  lg: "h-7 px-2.5 text-sm",
};

export function Badge({
  text,
  children,
  color = "gray",
  size = "md",
  className,
  style,
  ...props
}: BadgeProps) {
  const presetClass = PRESET[color as BadgeColor];
  const isPreset = Boolean(presetClass);

  // 커스텀 색상: 글자색은 검정과 섞어 밝은 색(yellow, gold 등)도 대비가 보장되게 하고,
  // 배경은 12%·테두리는 35% 톤으로 자동 계산한다.
  const customStyle: CSSProperties | undefined = isPreset
    ? undefined
    : {
        color: `color-mix(in oklch, ${color} 65%, black)`,
        backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
        borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
      };

  return (
    <span
      data-slot="badge"
      className={cn(
        "inline-flex items-center justify-center gap-1 rounded-full border font-medium whitespace-nowrap",
        SIZE[size],
        isPreset && presetClass,
        className
      )}
      style={{ ...customStyle, ...style }}
      {...props}
    >
      {text ?? children}
    </span>
  );
}
